// ============================================
// Analytics — fire-and-forget event tracking
//
// Writes rows to public.app_events. NEVER throws; if the network is
// down or the table is misconfigured, the call silently no-ops so
// production code paths aren't blocked.
//
// Use ONE event_name per discrete user action — don't smuggle state
// into event_props that should be its own event. Convention:
// snake_case names like welcome_flow_completed,
// announcement_cta_clicked.
// ============================================

import { supabase } from '../lib/supabase';
import * as Sentry from '@sentry/react';

export interface EventProps {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Insert an analytics event row. Always resolves — errors are logged
 * to Sentry as breadcrumbs (not exceptions) since one missed event
 * isn't worth paging on.
 */
export async function trackEvent(name: string, props?: EventProps): Promise<void> {
  try {
    // Get current user id if signed in; null otherwise (anonymous events allowed).
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id ?? null;

    const { error } = await supabase
      .from('app_events')
      .insert({
        user_id: userId,
        event_name: name,
        event_props: props ?? null,
      });

    if (error) {
      Sentry.addBreadcrumb({
        category: 'analytics',
        message: `trackEvent(${name}) failed`,
        level: 'warning',
        data: { error: error.message, props },
      });
    }
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: `trackEvent(${name}) threw`,
      level: 'warning',
      data: { err: String(err) },
    });
  }
}
